import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { desktop } from "@/controller";
import { DesktopHostContext } from "@/models/desktop-host-context";
import { useDesktopHost } from "@/models/useDesktopHost";
import { APP_DOWNLOAD_URLS } from "@/product-links";

export const DesktopHostProvider = ({ children }: { children: ReactNode }) => {
  const host = useDesktopHost(desktop.isAvailable);
  const { t } = useTranslation();

  if (host.needsUpdate)
    return (
      <main
        className="app-page-background fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-5 bg-[var(--bg-primary)] p-8 text-center text-[var(--text-primary)]"
        data-testid="desktop-update-required"
      >
        <h1 className="text-2xl font-semibold">
          {t("desktopSetup.updateTitle")}
        </h1>
        <p className="max-w-md text-sm text-[var(--text-secondary)]">
          {t("desktopSetup.updateBody")}
        </p>
        {host.update.state === "downloaded" ? (
          <button
            type="button"
            data-action-id="settings.desktop.installUpdate"
            onClick={() => void host.installUpdate()}
            className="rounded-full bg-[var(--accent)] px-6 py-3 font-semibold text-[var(--text-inverse)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
          >
            {t("desktopDictation.installUpdateTitle", {
              defaultValue: "Restart to update",
            })}
          </button>
        ) : (
          <a
            data-action-id="settings.apps.open"
            href={
              host.info?.platform === "macos"
                ? APP_DOWNLOAD_URLS.mac
                : APP_DOWNLOAD_URLS.windows
            }
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-[var(--accent)] px-6 py-3 font-semibold text-[var(--text-inverse)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
          >
            {t("desktopSetup.downloadUpdate")}
          </a>
        )}
      </main>
    );

  return (
    <DesktopHostContext.Provider value={host}>
      {children}
    </DesktopHostContext.Provider>
  );
};
